import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Camera, Mic, Send, Smile } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Conversation, Message } from "../../types";

interface MessagesAppProps {
  onClose: () => void;
}

export function MessagesApp({ onClose }: MessagesAppProps) {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [inputMessage, setInputMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch conversations from Convex
  const conversations = useQuery(api.messages.getConversations) || [];
  const messages = useQuery(
    api.messages.getMessages,
    selectedConversation ? { conversationId: selectedConversation as any } : "skip"
  ) || [];

  const selectedContact = conversations.find(
    (c) => c._id === selectedConversation
  );

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Format date for message bubbles
  const formatMessageTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Format relative time for conversation list
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } else if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return date.toLocaleDateString("en-US", { weekday: "short" });
    } else {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className="absolute inset-0 z-40 flex bg-black"
    >
      {/* Conversations List (iPad-style sidebar or iPhone when not in conversation) */}
      <div className={`flex flex-col ${selectedConversation ? "hidden" : "flex"} w-full border-r border-white/10 bg-black sm:flex sm:w-80`}>
        {/* Status Bar */}
        <div className="flex items-center justify-between px-6 pt-3 text-white">
          <span className="text-sm font-semibold">9:41</span>
          <div className="flex items-center gap-1">
            <div className="h-4 w-4 rounded-full border-2 border-white" />
            <div className="h-4 w-4 rounded-full border-2 border-white" />
            <div className="h-2 w-6 rounded-sm border-2 border-white bg-white" />
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center justify-between px-4 py-3">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="flex items-center gap-1 text-ios-blue"
          >
            <ChevronLeft className="h-6 w-6" />
            <span className="text-lg">Back</span>
          </motion.button>

          <h1 className="text-lg font-semibold text-white">Messages</h1>

          <div className="w-12" />
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-3">
          <div className="rounded-lg bg-white/10 px-4 py-2">
            <input
              type="text"
              placeholder="Search"
              className="w-full bg-transparent text-white placeholder-white/50 outline-none"
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500">
                <Send className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-white">No Messages Yet</h2>
              <p className="mt-1 text-sm text-white/50">
                Start a conversation to see messages here
              </p>
            </div>
          ) : (
            conversations.map((conversation, index) => (
              <motion.button
                key={conversation._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => setSelectedConversation(conversation._id)}
                className="flex w-full items-center gap-3 border-b border-white/5 px-4 py-3 hover:bg-white/5"
              >
                {/* Avatar */}
                <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-gray-400 to-gray-600">
                  <span className="text-lg font-medium text-white">
                    {conversation.contactName.charAt(0).toUpperCase()}
                  </span>
                  {conversation.unreadCount > 0 && (
                    <div className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-500 px-1.5 text-[10px] font-bold text-white">
                      {conversation.unreadCount}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex flex-1 flex-col overflow-hidden">
                  <div className="flex items-center justify-between">
                    <h3 className={`truncate text-base ${conversation.unreadCount > 0 ? "font-semibold text-white" : "text-white"}`}>
                      {conversation.contactName}
                    </h3>
                    <span className="ml-2 text-xs text-white/40">
                      {formatRelativeTime(conversation.lastMessageAt)}
                    </span>
                  </div>
                  <p className={`truncate text-sm ${conversation.unreadCount > 0 ? "font-medium text-white" : "text-white/50"}`}>
                    {conversation.messages[conversation.messages.length - 1]?.text || "No messages"}
                  </p>
                </div>
              </motion.button>
            ))
          )}
        </div>

        {/* Home Indicator */}
        <div className="pb-2 pt-1">
          <div className="mx-auto h-1 w-32 rounded-full bg-white/30" />
        </div>
      </div>

      {/* Message Thread View */}
      <AnimatePresence>
        {selectedConversation && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className="absolute inset-0 flex flex-col bg-black sm:relative sm:flex"
          >
            {/* Navigation Bar */}
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setSelectedConversation(null)}
                className="flex items-center gap-1 text-ios-blue sm:hidden"
              >
                <ChevronLeft className="h-6 w-6" />
                <span className="text-lg">Back</span>
              </motion.button>

              <div className="flex flex-1 flex-col items-center">
                <h2 className="text-base font-semibold text-white">
                  {selectedContact?.contactName}
                </h2>
                <span className="text-xs text-white/50">
                  {selectedContact?.isGroup ? "Group" : "iMessage"}
                </span>
              </div>

              <div className="w-12" />
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {messages.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <p className="text-white/50">No messages yet</p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {messages.map((message, index) => {
                    const isMe = message.sender === "me";
                    const showTime =
                      index === messages.length - 1 ||
                      new Date(messages[index + 1]?.timestamp).getTime() -
                        new Date(message.timestamp).getTime() >
                        5 * 60 * 1000;

                    return (
                      <div key={message._id}>
                        {/* Time separator */}
                        {showTime && (
                          <div className="my-4 flex items-center justify-center">
                            <span className="text-xs text-white/40">
                              {formatMessageTime(message.timestamp)}
                            </span>
                          </div>
                        )}

                        {/* Message Bubble */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                              isMe
                                ? "bg-ios-blue text-white"
                                : "bg-white/10 text-white"
                            }`}
                          >
                            <p className="text-base">{message.text}</p>
                            {message.attachments?.map((attachment, idx) => (
                              <div key={idx} className="mt-2">
                                {attachment.type === "image" && (
                                  <img
                                    src={attachment.url}
                                    alt="Attachment"
                                    className="max-h-48 rounded-lg"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="border-t border-white/10 px-4 py-3">
              <div className="flex items-center gap-3">
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10"
                >
                  <Camera className="h-5 w-5 text-white" />
                </motion.button>

                <div className="flex flex-1 items-center gap-2 rounded-full bg-white/10 px-4 py-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="iMessage"
                    className="flex-1 bg-transparent text-white outline-none"
                  />
                  <Smile className="h-5 w-5 text-white/50" />
                </div>

                {inputMessage ? (
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-ios-blue"
                  >
                    <Send className="h-4 w-4 text-white" />
                  </motion.button>
                ) : (
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10"
                  >
                    <Mic className="h-5 w-5 text-white" />
                  </motion.button>
                )}
              </div>
            </div>

            {/* Home Indicator */}
            <div className="pb-2 pt-1">
              <div className="mx-auto h-1 w-32 rounded-full bg-white/30" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}